import { styled } from "@mui/material/styles";
import PropTypes from "prop-types";
import { either, isEmpty, isNil, keys } from "ramda";
import { useCallback } from "react";

import { messages as onlyMessages } from "utils/transform-errors";

import { Box, Link, Paper, Typography } from "components/core";

const isReallyEmpty = either(isEmpty, isNil);

const StyledPaper = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderColor: theme.palette.error.main,
  borderWidth: "2px",
}));

const renderMessage = (message, dangerouslyAllowHtml) => {
  if (dangerouslyAllowHtml) {
    return <span dangerouslySetInnerHTML={{ __html: message }} />;
  }

  return message;
};

const EnrichedErrors = ({ errors, dangerouslyAllowHtml, linkErrors }) => {
  const messages = onlyMessages(errors);

  return linkErrors ? (
    <Box component="ul" lineHeight="1.4" sx={{ listStyleType: "none" }}>
      {keys(errors).map((key) => (
        <Box key={key} component="li" mt={1}>
          <Link color="error" href={`#${key}`}>
            {renderMessage(errors[key], dangerouslyAllowHtml)}
          </Link>
        </Box>
      ))}
    </Box>
  ) : (
    <Box
      component="ul"
      sx={{ listStyleType: "none" }}
      {...(messages.length > 1 && { sx: { listStyleType: "disc", ml: 3 } })}
    >
      {messages.map((message, idx) => (
        <li key={idx}>{renderMessage(message, dangerouslyAllowHtml)}</li>
      ))}
    </Box>
  );
};

EnrichedErrors.propTypes = {
  dangerouslyAllowHtml: PropTypes.bool.isRequired,
  errors: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.object,
  ]).isRequired,
  linkErrors: PropTypes.bool.isRequired,
};

const ValidationErrors = ({
  errors,
  dangerouslyAllowHtml = false,
  linkErrors = false,
  ...rest
}) => {
  const ref = useCallback(
    (node) => {
      // JSDom doesn't implement scrollIntoView, and on changes the previous instance will be called
      // with null
      if (node?.scrollIntoView && !isReallyEmpty(errors)) {
        window.requestAnimationFrame(() =>
          node?.scrollIntoView({ behavior: "smooth" }),
        );
      }
    },
    [errors],
  );

  if (isReallyEmpty(errors)) return null;

  return (
    <StyledPaper ref={ref} variant="outlined" {...rest}>
      <Typography component="div" fontWeight="bold" mb={1}>
        There’s a problem:
      </Typography>
      <EnrichedErrors
        dangerouslyAllowHtml={dangerouslyAllowHtml}
        errors={errors}
        linkErrors={linkErrors}
      />
    </StyledPaper>
  );
};

ValidationErrors.propTypes = {
  className: PropTypes.string,
  dangerouslyAllowHtml: PropTypes.bool,
  errors: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.string),
    PropTypes.object,
  ]),
  linkErrors: PropTypes.bool,
};

export default ValidationErrors;
